import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {adminRequest, buildQuery, getReturnedObject} from "../../api/adminApi";

type UserFilters = {
  token: string;
  searchParam?: string;
  status?: string;
  emailConfirmed?: string | boolean;
  pageNumber?: number;
  pageSize?: number;
};

type UserAction = {
  token: string;
  userId: string;
  email?: string;
  values?: any;
};

const pageInfoFrom = (data: any) => ({
  pageNumber: data?.pageNumber || 1,
  pageSize: data?.pageSize || 10,
  totalItems: data?.totalRecords || 0,
  totalPages: Math.ceil((data?.totalRecords || 0) / (data?.pageSize || 10)),
});

const initialState = {
  users: [] as any[],
  selectedUser: null as any,
  summary: null as any,
  loading: false,
  error: undefined as string | undefined,
  pageInfo: {pageNumber: 1, pageSize: 10, totalItems: 0, totalPages: 0},
};

export const getApplicationUsers = createAsyncThunk<any, UserFilters>(
  "userManagement/list",
  async (data) =>
    adminRequest(`/application-users${buildQuery({
      searchParam: data.searchParam,
      status: data.status,
      emailConfirmed: data.emailConfirmed,
      pageNumber: data.pageNumber || 1,
      pageSize: data.pageSize || 10,
    })}`, {token: data.token})
);

export const getApplicationUserById = createAsyncThunk<any, {token: string; userId: string}>(
  "userManagement/detail",
  async (data) => adminRequest(`/application-users/${data.userId}`, {token: data.token})
);

export const updateApplicationUser = createAsyncThunk<any, UserAction>(
  "userManagement/update",
  async (data) =>
    adminRequest(`/application-users/${data.userId}`, {
      method: "PATCH",
      token: data.token,
      body: JSON.stringify(data.values || {}),
    })
);

export const resendApplicationUserVerification = createAsyncThunk<any, UserAction>(
  "userManagement/resendVerification",
  async (data) =>
    adminRequest(`/application-users/${data.userId}/resend-verification`, {
      method: "POST",
      token: data.token,
    })
);

export const activateApplicationUser = createAsyncThunk<any, UserAction>(
  "userManagement/activate",
  async (data) =>
    adminRequest(`/application-users/${data.userId}/activate`, {
      method: "POST",
      token: data.token,
      body: JSON.stringify({email: data.email}),
    })
);

export const deactivateApplicationUser = createAsyncThunk<any, UserAction>(
  "userManagement/deactivate",
  async (data) =>
    adminRequest(`/application-users/${data.userId}/deactivate`, {
      method: "POST",
      token: data.token,
    })
);

export const deleteApplicationUser = createAsyncThunk<any, UserAction>(
  "userManagement/delete",
  async (data) =>
    adminRequest(`/application-users/${data.userId}/delete`, {
      method: "POST",
      token: data.token,
    })
);

export const unlockApplicationUserLiquidations = createAsyncThunk<any, UserAction>(
  "userManagement/unlockLiquidations",
  async (data) =>
    adminRequest(`/application-users/${data.userId}/unlock-liquidations`, {
      method: "POST",
      token: data.token,
      body: JSON.stringify({reason: data.values?.reason}),
    })
);

export const unlockApplicationUserBillPayments = createAsyncThunk<any, UserAction>(
  "userManagement/unlockBillPayments",
  async (data) =>
    adminRequest(`/application-users/${data.userId}/unlock-bill-payments`, {
      method: "POST",
      token: data.token,
      body: JSON.stringify({reason: data.values?.reason}),
    })
);

const userManagementSlice = createSlice({
  name: "userManagement",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getApplicationUsers.fulfilled, (state, action) => {
        const report = getReturnedObject(action.payload);
        state.loading = false;
        state.summary = report;
        state.users = (report?.users || []).map((item: any) => ({...item, key: item.id}));
        state.pageInfo = pageInfoFrom(report);
      })
      .addCase(getApplicationUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = getReturnedObject(action.payload);
      })
      .addCase(getApplicationUserById.pending, (state) => {
        state.loading = true;
        state.error = undefined;
        state.selectedUser = null;
      })
      .addMatcher(
        (action) =>
          action.type.startsWith("userManagement/") &&
          action.type.endsWith("/fulfilled") &&
          !["userManagement/list/fulfilled", "userManagement/detail/fulfilled"].includes(action.type),
        (state, action: any) => {
          state.loading = false;
          const updated = getReturnedObject(action.payload);
          if (updated?.id) {
            state.selectedUser = updated;
            state.users = state.users.map((user) => user.id === updated.id ? {...updated, key: updated.id} : user);
          }
        }
      )
      .addMatcher(
        (action) => action.type.startsWith("userManagement/") && action.type.endsWith("/pending"),
        (state) => {
          state.loading = true;
          state.error = undefined;
        }
      )
      .addMatcher(
        (action) => action.type.startsWith("userManagement/") && action.type.endsWith("/rejected"),
        (state, action: any) => {
          state.loading = false;
          state.error = action.error?.message;
        }
      );
  },
});

export default userManagementSlice.reducer;
